// TrendAura — Motion One enhancement layer (Framer Motion vanilla via ESM).
// Terracotta palette (#B8543A) only. Respects prefers-reduced-motion.
import { animate, inView, scroll, stagger } from "https://cdn.jsdelivr.net/npm/motion@11.11.17/+esm";

const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ---------------------------------------------------------------------------
// 1) Hero parallax — translate bg image y as section scrolls past
// ---------------------------------------------------------------------------
(() => {
  const section = document.querySelector(".ta-hero");
  const img = section?.querySelector(".ta-hero__img");
  if (!section || !img || reduced) return;
  scroll(
    animate(img, { y: [0, -60], scale: [1, 1.05] }, { ease: "linear" }),
    { target: section, offset: ["start end", "end start"] }
  );

  // Staggered entry for eyebrow / title / sub / CTA
  const items = section.querySelectorAll(
    ".ta-hero__eyebrow, .ta-hero__title, .ta-hero__sub, .ta-hero__cta"
  );
  if (items.length) {
    animate(
      [...items],
      { opacity: [0, 1], y: [18, 0] },
      { duration: 0.9, delay: stagger(0.08, { start: 0.1 }), easing: [0.22, 1, 0.36, 1] }
    );
  }
})();

// ---------------------------------------------------------------------------
// 2) Editorial — clip-path reveal with spring + image parallax
// ---------------------------------------------------------------------------
(() => {
  const section = document.querySelector(".ta-editorial");
  const img = section?.querySelector(".ta-editorial__img");
  const card = section?.querySelector(".ta-editorial__card");
  if (!section) return;

  if (img && !reduced) {
    scroll(
      animate(img, { y: [-30, 30], scale: [1.04, 1] }, { ease: "linear" }),
      { target: section, offset: ["start end", "end start"] }
    );
  }

  if (card && !reduced) {
    card.style.clipPath = "inset(0 100% 0 0)";
    inView(card, () => {
      animate(card, { clipPath: "inset(0 0% 0 0)" }, { duration: 1.0, easing: [0.65, 0.05, 0.36, 1] });
      return () => {};
    }, { amount: 0.4 });
  }
})();

// ---------------------------------------------------------------------------
// 3) Product rows — stagger reveal with Motion (replaces CSS fallback once in view)
// ---------------------------------------------------------------------------
(() => {
  if (reduced) return;
  document.querySelectorAll(".ta-prow__grid").forEach((grid) => {
    const items = grid.querySelectorAll(".ta-prow__item");
    if (!items.length) return;
    // Let CSS handle initial state; Motion refines the transition
    inView(grid, () => {
      grid.classList.add("is-in"); // keep CSS fallback compatible
      animate(
        [...items],
        { opacity: [0, 1], y: [20, 0] },
        { duration: 0.75, delay: stagger(0.06), easing: [0.22, 1, 0.36, 1] }
      );
      return () => {};
    }, { amount: 0.15 });
  });
})();

// ---------------------------------------------------------------------------
// 4) Pillars — subtle terracotta glow follows hovered card
// ---------------------------------------------------------------------------
(() => {
  const section = document.querySelector(".ta-pillars");
  if (!section || reduced) return;

  // Inject a single glow layer lazily (no HTML change needed in section)
  const grid = section.querySelector(".ta-pillars__grid");
  if (!grid) return;
  grid.style.position = "relative";
  const glow = document.createElement("div");
  glow.className = "ta-pillars__glow";
  glow.setAttribute("aria-hidden", "true");
  Object.assign(glow.style, {
    position: "absolute",
    left: "0", top: "0",
    width: "0", height: "0",
    pointerEvents: "none",
    background: "radial-gradient(closest-side, rgba(184,84,58,0.14), transparent 70%)",
    filter: "blur(22px)",
    opacity: "0",
    zIndex: "0",
    borderRadius: "2px",
  });
  grid.prepend(glow);

  grid.querySelectorAll(".ta-pillars__item").forEach((card) => {
    card.style.position = "relative";
    card.style.zIndex = "1";
    card.addEventListener("pointerenter", () => {
      const r = card.getBoundingClientRect();
      const gr = grid.getBoundingClientRect();
      animate(
        glow,
        {
          left: r.left - gr.left + "px",
          top: r.top - gr.top + "px",
          width: r.width + "px",
          height: r.height + "px",
          opacity: 1,
        },
        { duration: 0.45, easing: [0.22, 1, 0.36, 1] }
      );
    });
  });
  grid.addEventListener("pointerleave", () => {
    animate(glow, { opacity: 0 }, { duration: 0.3 });
  });
})();

// ---------------------------------------------------------------------------
// 5) Trust bar — subtle stagger on enter
// ---------------------------------------------------------------------------
(() => {
  if (reduced) return;
  const grid = document.querySelector(".ta-trust__grid");
  if (!grid) return;
  const items = grid.querySelectorAll(".ta-trust__item");
  if (!items.length) return;
  inView(grid, () => {
    animate(
      [...items],
      { opacity: [0, 1], x: [-12, 0] },
      { duration: 0.7, delay: stagger(0.09), easing: [0.22, 1, 0.36, 1] }
    );
    return () => {};
  }, { amount: 0.3 });
})();

// ---------------------------------------------------------------------------
// 6) Social carousel cards — fade-up as they enter
// ---------------------------------------------------------------------------
(() => {
  if (reduced) return;
  const cards = document.querySelectorAll(".ta-social__card");
  cards.forEach((card, i) => {
    inView(card, () => {
      animate(
        card,
        { opacity: [0, 1], y: [20, 0] },
        { duration: 0.7, delay: Math.min(i % 4, 3) * 0.06, easing: [0.22, 1, 0.36, 1] }
      );
      return () => {};
    }, { amount: 0.25 });
  });
})();
